import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';

export default class LogoService {
  private static LOGO_DIRECTORY = 'logo';
  private static logoCache: Map<string, string> = new Map();

  private getBaseUrl(): string {
    return process.env.VUE_APP_API_URL || process.env.VUE_APP_API_EXT_URL || '';
  }

  private getServerLogoUrl(shortName: string): string {
    return `${this.getBaseUrl()}api/program-logos/${encodeURIComponent(shortName)}`;
  }

  private getFallback(shortName: string): string {
    try {
      return require(`@/theme/programs/${shortName}.png`);
    } catch {
      return require('@/theme/liga-logo-white.png');
    }
  }

  /**
   * Get program logo URL: server logo (api/program-logos/:shortName) or fallback from theme.
   */
  async getProgramLogo(shortName: string | undefined, forceRefresh = false): Promise<string> {
    if (!shortName || shortName.trim() === '') {
      return require('@/theme/liga-logo-white.png');
    }

    if (forceRefresh) {
      const old = LogoService.logoCache.get(shortName);
      if (old?.startsWith('blob:')) URL.revokeObjectURL(old);
      LogoService.logoCache.delete(shortName);
      if (Capacitor.isNativePlatform()) {
        try {
          await Filesystem.deleteFile({
            path: `${LogoService.LOGO_DIRECTORY}/${shortName}.png`,
            directory: Directory.Data,
          });
        } catch {
          /* ignore */
        }
      }
    }

    if (!forceRefresh && LogoService.logoCache.has(shortName)) {
      const cached = LogoService.logoCache.get(shortName);
      if (cached) return cached;
    }

    const serverUrl = this.getServerLogoUrl(shortName);

    try {
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;
      const response = await CapacitorHttp.request({
        method: 'HEAD',
        url: serverUrl,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.status === 200) {
        LogoService.logoCache.set(shortName, serverUrl);
        return serverUrl;
      }
    } catch (e) {
      console.warn(`Logo HEAD failed for ${shortName}:`, e);
    }
    const fallback = this.getFallback(shortName);
    LogoService.logoCache.set(shortName, fallback);
    return fallback;
  }

  /**
   * Return server logo URL without checking existence (e.g. for img src with @error fallback).
   */
  getProgramLogoServerUrl(shortName: string | undefined): string {
    if (!shortName || shortName.trim() === '') return require('@/theme/liga-logo-white.png');
    return this.getServerLogoUrl(shortName);
  }

  /**
   * Download all program logos (mobile: pre-populate memory cache).
   */
  async downloadAllProgramLogos(programs: Array<{ shortName: string }>): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await Promise.allSettled(programs.map((p) => this.getProgramLogo(p.shortName, false)));
  }

  /**
   * Refresh a specific logo (clear cache and re-fetch).
   */
  async refreshLogo(shortName: string): Promise<string> {
    return this.getProgramLogo(shortName, true);
  }

  async refreshAllLogos(programs: Array<{ shortName: string }>): Promise<void> {
    await Promise.allSettled(programs.map((p) => this.refreshLogo(p.shortName)));
  }

  async clearCache(): Promise<void> {
    LogoService.logoCache.forEach((url) => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    });
    LogoService.logoCache.clear();
    if (Capacitor.isNativePlatform()) {
      try {
        const { files } = await Filesystem.readdir({
          path: LogoService.LOGO_DIRECTORY,
          directory: Directory.Data,
        });
        for (const file of files) {
          await Filesystem.deleteFile({
            path: `${LogoService.LOGO_DIRECTORY}/${file}`,
            directory: Directory.Data,
          }).catch(() => {});
        }
      } catch {
        /* ignore */
      }
    }
  }
}
